import { Link, useNavigate } from "@tanstack/react-router";
import React, { useEffect, useState } from "react";
import { JobPostForm } from "@/features/job-post/forms/job-post-form";
import { Button, buttonVariants } from "@/components/ui/button";
import { AlertCircle, ArrowLeft, Eye } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { JobPostProvider } from "@/features/job-post/matching/job-post-provider";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  JobPostSchemaType,
  RequirementRow,
  makeFreeTextRow,
  makeRequirementKey,
  serializeRequirementValue,
} from "../utils/job-data";
import {
  JobPostRequirementType,
  JobPostRequirementOperator,
  JobPostRequirementSeverity,
  JobPostVisibility,
  PracticeSetting,
} from "@api/shared-runtime/enums";
import type { MatchStrictness } from "../utils/job-data";
import { buildRequirementListLabel } from "../utils/requirement-labels";
import { useTRPC } from "@/trpc";
import Loading from "@/components/Loading";
import { queryClient } from "@/ajax/queryClient";
import { useOrganization } from "@/hooks/use-organization";
import { useOrgPath } from "@/hooks/useOrgPath";
import {
  RecordLoadFailed,
  RecordNotInBusinessUnit,
} from "@/components/page-message";
import {
  isRecordUnavailableError,
  retryUnlessRecordUnavailable,
} from "@/common/utils/record-unavailable-error";

const requirementTypesWithDerivedLabel = new Set<JobPostRequirementType>([
  JobPostRequirementType.FreeText,
  JobPostRequirementType.StateLicense,
]);

const SAVE_FAILED_MESSAGE = "Failed to save the job post. Please try again.";
const INVALID_INPUT_MESSAGE =
  "Some job post details are invalid. Please review the form and try again.";

/**
 * tRPC reports input-validation failures with the raw Zod issue list
 * serialized as the message, which is unreadable in the save alert.
 */
function isSerializedValidationIssues(message: string): boolean {
  try {
    return Array.isArray(JSON.parse(message));
  } catch {
    return false;
  }
}

function getSaveErrorMessage(error: unknown): string {
  if (!(error instanceof Error)) {
    return SAVE_FAILED_MESSAGE;
  }
  if (isSerializedValidationIssues(error.message)) {
    return INVALID_INPUT_MESSAGE;
  }
  return error.message;
}

const newJobData: JobPostSchemaType = {
  title: "",
  externalId: "",
  visibility: JobPostVisibility.Public,
  startDateType: "immediate",
  endDateType: "none",
  locationType: "facility",
  employmentTypeIds: [],
  professionalTypeSeverity: "none",
  specialtySeverity: "none",
};

// The form holds the practice setting as an id, while the card expects the
// API's { displayText } object, so the preview's facility line falls back to
// the facility type as it always has.
function previewJobData(formData: JobPostSchemaType) {
  return { ...formData, practiceSetting: undefined, eligibility: undefined };
}

export function JobPost({ jobId }: { jobId?: string }) {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const orgPath = useOrgPath();
  const { selectedOrgBusinessUnit } = useOrganization();
  const {
    data: jobQuery,
    isLoading: jobQueryLoading,
    isFetching: jobQueryFetching,
    error: jobQueryError,
    refetch: refetchJobQuery,
  } = useQuery(
    trpc.jobPost.orgGet.queryOptions(
      {
        id: jobId ?? "",
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      },
      {
        enabled: !!jobId,
        retry: retryUnlessRecordUnavailable,
        staleTime: 0,
        refetchOnMount: "always",
      },
    ),
  );

  const jobMutation = useMutation(
    trpc.jobPost.save.mutationOptions({
      // Note: we do NOT navigate here. Submit handler waits for BOTH the
      // job-post save AND the requirements save to finish, then navigates
      // explicitly. Navigating mid-handler would unmount the form before
      // the requirements mutation could fire its fetch (lost-write bug).
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.jobPost.getMany.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.jobPost.orgGet.queryKey({
              id: jobId ?? "",
              organizationBusinessUnitId: selectedOrgBusinessUnit.id,
            }),
          }),
        ]);
      },
    }),
  );
  const [formData, setFormData] = useState<JobPostSchemaType | null>(
    jobId ? null : newJobData,
  );
  const hasHydratedInitialFormRef = React.useRef(!jobId);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<{
    data: JobPostSchemaType;
    isDraft: boolean;
  } | null>(null);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    hasHydratedInitialFormRef.current = !jobId;
    setFormData(jobId ? null : newJobData);
  }, [jobId]);

  useEffect(() => {
    if (!jobQuery) {
      return;
    }
    if (hasHydratedInitialFormRef.current) {
      return;
    }
    if (jobId && jobQueryFetching) {
      return;
    }

    const jobData = jobQuery;
    const savedRequirements = jobData.requirements ?? [];

    // Determine location type based on existing data
    let locationType: "facility" | "city-state" | "remote" = "facility";
    if (jobData.remote) {
      locationType = "remote";
    } else if (jobData.facility) {
      locationType = "facility";
    } else if (jobData.city || jobData.stateCode) {
      locationType = "city-state";
    } else {
      locationType = "facility"; // default
    }

    // Build the unified requirements list from saved rows.
    // ProfessionalType and Specialty rows are extracted to set the top-of-form
    // strictness radios. All other rows are passed to the requirements editor.
    // Legacy fallback: if there are no structured rows but the job post has
    // requiredQualifications text[], synthesize free_text rows for each entry.
    let requirements: RequirementRow[];
    let professionalTypeSeverity: MatchStrictness = "none";
    let specialtySeverity: MatchStrictness = "none";

    if (savedRequirements && savedRequirements.length > 0) {
      const editorRows: RequirementRow[] = [];

      for (const r of savedRequirements) {
        let value: import("../utils/job-data").RequirementValue;
        const v = r.value as Record<string, unknown>;
        const type = r.type as JobPostRequirementType;

        switch (type) {
          case JobPostRequirementType.FreeText:
            value = { kind: "free_text", text: (v.text as string) ?? "" };
            break;
          case JobPostRequirementType.StateLicense:
            value = {
              kind: "state_license",
              states: (v.states as string[]) ?? [],
            };
            break;
          case JobPostRequirementType.ProfessionalType:
            // Extract to top-of-form field; don't add to editor
            if (r.severity === JobPostRequirementSeverity.Block) {
              professionalTypeSeverity = "block";
            } else if (
              r.severity === JobPostRequirementSeverity.Flag &&
              professionalTypeSeverity === "none"
            ) {
              professionalTypeSeverity = "flag";
            }
            continue;
          case JobPostRequirementType.Specialty:
            // Extract to top-of-form field; don't add to editor
            if (r.severity === JobPostRequirementSeverity.Block) {
              specialtySeverity = "block";
            } else if (
              r.severity === JobPostRequirementSeverity.Flag &&
              specialtySeverity === "none"
            ) {
              specialtySeverity = "flag";
            }
            continue;
          default:
            // Unknown type — skip to avoid corrupting the editor
            continue;
        }
        editorRows.push({
          _key: makeRequirementKey(),
          type,
          operator: r.operator as JobPostRequirementOperator,
          severity: r.severity as JobPostRequirementSeverity,
          displayLabel: r.displayLabel,
          value,
        });
      }

      requirements = editorRows;
    } else if (savedRequirements !== undefined) {
      // savedRequirements has resolved but is empty — check legacy field
      requirements = (jobData.requiredQualifications ?? []).map((text) =>
        makeFreeTextRow(text),
      );
    } else {
      // savedRequirements still loading — leave requirements undefined so
      // the form doesn't flash empty then fill in
      requirements = [];
    }

    setFormData({
      id: jobData.id,
      title: jobData.title,
      externalId: jobData.externalId || "",
      locationType,
      facility: jobData.facility || undefined,
      wageMinAmount: jobData.wageMinAmount || undefined,
      wageMaxAmount: jobData.wageMaxAmount || undefined,
      wageFrequencyId: jobData.wageFrequencyId || null,
      guaranteedBasePay: jobData.guaranteedBasePay || false,
      guaranteedBasePayDescription: jobData.guaranteedBasePayDescription || "",
      specialties: jobData?.specialties || [],
      jobDescription: jobData?.description || "",
      responsibilities: jobData?.responsibilities || [],
      preferredQualifications: jobData?.preferredQualifications || [],
      requirements,
      professionalTypeSeverity,
      specialtySeverity,
      scheduleDescription: jobData?.scheduleDescription || "",
      startDateType: jobData?.startDate ? "specific" : "immediate",
      startDate: jobData.startDate || undefined,
      endDateType: jobData.endDate ? "specific" : "none",
      endDate: jobData.endDate || undefined,
      employmentTypeIds: jobData?.employmentTypeIds ?? [],
      healthcareProfessionalTypes:
        jobData.healthcareProfessionalTypes || undefined,
      publishedAt: jobData?.publishedAt || undefined,
      visibility: jobData.visibility ?? JobPostVisibility.Public,
      city: jobData.city || null,
      stateCode: jobData.stateCode || null,
      cityLocation:
        jobData.city && jobData.stateCode
          ? {
              name: `${jobData.city}, ${jobData.stateCode}, USA`,
              lat: jobData.lat ?? undefined,
              lng: jobData.lng ?? undefined,
            }
          : null,
      organizationNotes: jobData?.organizationNotes || undefined,
      hiringProcessId: jobData.hiringProcessId ?? null,
      practiceSetting:
        locationType === "remote"
          ? PracticeSetting.RemoteVirtual
          : jobData.practiceSettingId || null,
      organizationBusinessUnitId:
        jobData?.organizationBusinessUnit?.id ?? selectedOrgBusinessUnit.id,
    });
    hasHydratedInitialFormRef.current = true;
  }, [jobId, jobQueryFetching, jobQuery, selectedOrgBusinessUnit.id]);

  const handleFormUpdate = ({
    data,
    isDraft,
  }: {
    data: JobPostSchemaType;
    isDraft: boolean;
  }) => {
    setFormData(data);
  };

  const handleSave = async ({
    data,
    isDraft,
  }: {
    data: JobPostSchemaType;
    isDraft: boolean;
  }) => {
    // If we're publishing and there's no publishedAt date yet, show confirmation dialog
    if (!isDraft && !data.publishedAt) {
      setSaveErrorMessage(null);
      setPendingSaveData({ data, isDraft });
      setIsPublishConfirmOpen(true);
      return;
    }

    // Otherwise proceed with save
    await submitJobPost(data, isDraft);
  };

  const submitJobPost = async (data: JobPostSchemaType, isDraft: boolean) => {
    setSaveErrorMessage(null);

    // Prepare location data based on location type
    let facilityId: string | null;
    let city: string | null;
    let stateCode: string | null;
    let remote: boolean | null;
    const isRemoteLocation = data.locationType === "remote";

    if (data.locationType === "facility" && data.facility) {
      facilityId = data.facility.id;
      // Clear city/state and remote when using facility
      city = null;
      stateCode = null;
      remote = null;
    } else if (data.locationType === "city-state") {
      // Clear facility and remote when using city/state
      facilityId = null;
      // Parse city and state from cityLocation.name (format: "City, State, USA")
      if (data.cityLocation?.name) {
        const parts = data.cityLocation.name.split(", ");
        city = parts[0] || null;
        stateCode = parts[1] || null;
      } else {
        city = null;
        stateCode = null;
      }
      remote = false;
    } else if (data.locationType === "remote") {
      // Clear facility and city/state for remote, set remote to true
      facilityId = null;
      city = null;
      stateCode = null;
      remote = true;
    } else {
      throw new Error("Invalid location type");
    }

    const practiceSettingId = isRemoteLocation
      ? PracticeSetting.RemoteVirtual
      : data.practiceSetting || undefined;

    try {
      const orgBuId =
        data.organizationBusinessUnitId ?? selectedOrgBusinessUnit.id;

      // Assemble the unified requirements list: editor rows + synthesized
      // rows from the top-of-form professional-type / specialty
      // match-strictness fields. Sent to the server in the SAME save call
      // so the job-post upsert and the requirements replace-all happen
      // inside one DB transaction (atomic).
      const requirementRows: RequirementRow[] = [...(data.requirements ?? [])];

      const profTypes = data.healthcareProfessionalTypes ?? [];
      const profSeverity = data.professionalTypeSeverity ?? "none";
      if (profSeverity !== "none" && profTypes.length > 0) {
        const typeNames = profTypes.map((t) => t.name);
        requirementRows.push({
          _key: makeRequirementKey(),
          type: JobPostRequirementType.ProfessionalType,
          operator: JobPostRequirementOperator.HasAny,
          severity:
            profSeverity === "block"
              ? JobPostRequirementSeverity.Block
              : JobPostRequirementSeverity.Flag,
          displayLabel: buildRequirementListLabel({
            prefix: "Must be a",
            names: typeNames,
          }),
          value: { kind: "professional_type", ids: profTypes.map((t) => t.id) },
        });
      }

      const specialties = data.specialties ?? [];
      const specSeverity = data.specialtySeverity ?? "none";
      if (specSeverity !== "none" && specialties.length > 0) {
        const specialtyNames = specialties.map((s) => s.name);
        requirementRows.push({
          _key: makeRequirementKey(),
          type: JobPostRequirementType.Specialty,
          operator: JobPostRequirementOperator.HasAny,
          severity:
            specSeverity === "block"
              ? JobPostRequirementSeverity.Block
              : JobPostRequirementSeverity.Flag,
          displayLabel: buildRequirementListLabel({
            prefix: "Must specialize in",
            names: specialtyNames,
          }),
          value: { kind: "specialty", ids: specialties.map((s) => s.id) },
        });
      }

      // Save the core job post fields + the requirements in one atomic
      // transaction. The server dual-writes free_text rows to
      // jobPost.requiredQualifications so existing readers (AI evaluator,
      // job-detail card) keep seeing the free-text values — don't send
      // requiredQualifications here, it would conflict.
      const savedPost = await jobMutation.mutateAsync({
        id: jobId,
        title: data.title,
        description: data.jobDescription,
        preferredQualifications: data.preferredQualifications,
        responsibilities: data.responsibilities,
        healthcareProfessionalTypeIds: data.healthcareProfessionalTypes?.map(
          (type) => type.id,
        ),
        wageMinAmount: data.wageMinAmount?.toString(),
        wageMaxAmount: data.wageMaxAmount?.toString(),
        wageFrequencyId: data.wageFrequencyId ?? undefined,
        scheduleDescription: data.scheduleDescription,
        startDate: data.startDate,
        endDate: data.endDate,
        employmentTypeIds: data.employmentTypeIds,
        public: true,
        visibility: data.visibility,
        specialties: data?.specialties?.map((s) => s.id) || [],
        organizationBusinessUnitId: orgBuId,
        guaranteedBasePay: data.guaranteedBasePay,
        guaranteedBasePayDescription: data.guaranteedBasePayDescription,
        externalId: data.externalId?.trim() || undefined,
        facilityId,
        city,
        stateCode,
        lat: data.cityLocation?.lat ?? null,
        lng: data.cityLocation?.lng ?? null,
        remote,
        organizationNotes: data.organizationNotes,
        hiringProcessId: data.hiringProcessId,
        publish: !isDraft,
        practiceSettingId,
        requirements: requirementRows.map((r) => ({
          type: r.type,
          operator: r.operator,
          severity: r.severity,
          // The server derives free-text and state-license labels from the
          // value. Free text can be longer than the display-label limit, so
          // sending it here would fail validation.
          displayLabel: requirementTypesWithDerivedLabel.has(r.type)
            ? undefined
            : r.displayLabel,
          value: serializeRequirementValue(r.value),
        })),
      });

      await queryClient.invalidateQueries({
        queryKey: trpc.jobPost.orgGet.queryKey({
          id: savedPost.id,
          organizationBusinessUnitId: orgBuId,
        }),
      });

      navigate({
        to: orgPath("/jobs/post/dashboard/" + savedPost.id),
      });
    } catch (error) {
      setSaveErrorMessage(getSaveErrorMessage(error));
      throw error;
    }
  };

  const handleConfirmPublish = async () => {
    if (pendingSaveData) {
      try {
        await submitJobPost(pendingSaveData.data, pendingSaveData.isDraft);
        setIsPublishConfirmOpen(false);
        setPendingSaveData(null);
      } catch {
        setIsPublishConfirmOpen(false);
        setPendingSaveData(null);
      }
    }
  };

  if (
    jobQueryLoading ||
    (!!jobId && !hasHydratedInitialFormRef.current && jobQueryFetching)
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading className="h-20 w-20" />
      </div>
    );
  }

  // orgGet answers NOT_FOUND when the job isn't in the selected business
  // unit, e.g. after switching business units while editing.
  if (jobId && jobQueryError && !isRecordUnavailableError(jobQueryError)) {
    return (
      <RecordLoadFailed
        recordLabel="job post"
        onRetry={() => void refetchJobQuery()}
      />
    );
  }
  if (jobId && !jobQuery) {
    return (
      <RecordNotInBusinessUnit
        title="Job post not found"
        recordLabel="job post"
        back={{ to: orgPath("/jobs/post"), label: "Back to job posts" }}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl bg-slate-50 px-4 py-6 sm:px-6 sm:py-8">
      {/* Back Button */}
      {jobId ? (
        <div className="mb-4">
          <Link
            to={orgPath("/jobs/post/dashboard/" + jobId)}
            className={buttonVariants({
              variant: "ghost",
              size: "sm",
              className:
                "gap-2 text-primary hover:bg-primary/5 hover:text-primary",
            })}
          >
            <ArrowLeft size={16} className="mr-1.5" />
            Back to Dashboard
          </Link>
        </div>
      ) : null}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
          Create Job Post
        </h1>
      </div>

      {saveErrorMessage ? (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Unable to save job post</AlertTitle>
          <AlertDescription>{saveErrorMessage}</AlertDescription>
        </Alert>
      ) : null}

      {/* Mobile Preview Dialog Button */}
      <div className="mb-6 lg:hidden">
        <Dialog
          open={isPreviewDialogOpen}
          onOpenChange={setIsPreviewDialogOpen}
        >
          <DialogTrigger asChild>
            <Button
              variant="outline"
              className="w-full gap-2 font-semibold sm:w-auto"
              disabled={!formData}
            >
              <Eye className="h-4 w-4" />
              Preview
            </Button>
          </DialogTrigger>
          <DialogContent className="[&.dialog-content]:max-w-[min(38rem,calc(100vw-2rem))] [&.dialog-content]:max-h-[calc(100dvh-2rem)] gap-4 overflow-y-auto rounded-2xl border-slate-200 bg-slate-50 sm:rounded-2xl">
            <DialogHeader>
              <DialogTitle>Job Posting Preview</DialogTitle>
            </DialogHeader>
            {formData && <JobPostProvider jobData={previewJobData(formData)} />}
          </DialogContent>
        </Dialog>
      </div>

      {/* Publish Confirmation Dialog */}
      <Dialog
        open={isPublishConfirmOpen}
        onOpenChange={(open) => {
          setIsPublishConfirmOpen(open);
          if (!open) {
            setPendingSaveData(null);
            // Intentionally do NOT clear saveErrorMessage here — the dialog
            // closes after a failed save, and clearing the error would hide
            // the only signal the user has that the save failed.
          }
        }}
      >
        <DialogContent className="[&.dialog-content]:max-w-[min(32rem,calc(100vw-2rem))] [&.dialog-content]:max-h-[calc(100dvh-2rem)] gap-4 overflow-y-auto rounded-2xl border-slate-200 bg-white sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle>Publish Job Post</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-slate-600">
              {selectedOrgBusinessUnit?.skipJobPostReview
                ? "Are you sure you want to publish this job post? It will be visible to healthcare providers on the platform immediately."
                : "Are you sure you want to submit this job post for review? Once submitted, it will be reviewed by Saile staff before being published to healthcare providers on the platform."}
            </DialogDescription>
          </DialogHeader>
          {!selectedOrgBusinessUnit?.skipJobPostReview && (
            <div className="flex items-center gap-2 py-3">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              <p className="text-sm text-amber-700">
                This action will submit your job post for review by Saile staff.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsPublishConfirmOpen(false)}
              disabled={jobMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmPublish}
              className="font-semibold"
              disabled={jobMutation.isPending}
            >
              {jobMutation.isPending ? "Publishing..." : "Publish Job Post"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main content with responsive layout */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Form section - takes full width on mobile, partial on desktop */}
        <div className="w-full min-w-0 lg:flex-[7]">
          {formData ? (
            <JobPostForm
              defaultValues={formData}
              onSave={handleSave}
              onChange={handleFormUpdate}
              jobPostId={jobId}
              requirementsLoading={
                !!jobId && !hasHydratedInitialFormRef.current
              }
              saveError={saveErrorMessage}
              onCancel={() => {
                navigate({
                  to: orgPath("/jobs/post"),
                });
              }}
            />
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Loading className="h-20 w-20" />
            </div>
          )}
        </div>

        {/* Desktop Preview section - hidden on mobile */}
        <div className="sticky top-6 hidden min-w-0 self-start lg:block lg:flex-[5]">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-xs overflow-auto max-h-[calc(100vh-120px)]">
            <div className="p-5 bg-white border-b border-slate-200 flex justify-between items-center">
              <h2 className="font-semibold text-slate-800">Preview</h2>
              <span className="text-xs bg-primary/5 text-primary px-2 py-1 rounded-full">
                Live
              </span>
            </div>
            <div className="bg-slate-50/60 p-4 xl:p-5">
              {formData ? (
                <JobPostProvider jobData={previewJobData(formData)} />
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <p>Fill out the form to see a preview</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
